import type { ReactNode } from 'react'
import { useState, useRef } from 'react'

import {
  faAngleUp,
  faAngleDown,
  faCircleInfo,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { useIsomorphicLayoutEffect, useLocalStorage } from 'react-use'
import { styled } from 'styled-components'

import { UnstyledButton } from '@b-stock/fe-core/bstock-next/components'
import { Tooltip, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

type ExpandProps = {
  afterExpandAnimation?: (target: HTMLButtonElement) => void
  expandedOnInit: boolean
  label: string
  onExpand?: () => void
  shouldUseExpandMemory: boolean
  tooltip?: string
}

const Body = styled.div<{ $inSidebar?: boolean }>`
  display: grid;
  gap: 0.75rem;
  /* Let the track shrink below CategoricalFilter's two-column intrinsic width */
  grid-template-columns: minmax(0, 1fr);
  width: ${(o) => o.$inSidebar && '11.75rem'};

  &:not(:first-child) {
    border-top: 1px solid ${Colors.Semantic.Neutral.medium_grey};
    padding-top: 1.5rem;
  }
`

export const FilterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const Summary = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
`

const Label = styled.span`
  ${Typography.Subtitle2}
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  color: ${Colors.Semantic.Neutral.black};
`

const TooltipIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Neutral.dark_grey};
  cursor: pointer;
  font-size: 0.875rem;
`

const Details = styled.div`
  & > div:last-child {
    border-bottom: none;
  }
  & > input {
    box-sizing: border-box;
  }
`

const useExpand = ({
  afterExpandAnimation,
  expandedOnInit,
  label,
  onExpand,
  shouldUseExpandMemory,
}: ExpandProps) => {
  const [isExpandedStored, setIsExpandedStored] = useLocalStorage<boolean>(
    `Expand:${label}`
  )
  const [isExpanded, setIsExpandedEphemeral] = useState(expandedOnInit)

  const setIsExpanded = shouldUseExpandMemory
    ? (value: boolean) => {
        setIsExpandedStored(value)
        setIsExpandedEphemeral(value)
      }
    : setIsExpandedEphemeral

  useIsomorphicLayoutEffect(
    () => {
      // Why take this extra step instead of just using isExpandedStored immediately?
      // To avoid "Hydration failed ... UI does not match ... server" errors.
      // Since we store the memory in local storage, we don't have access to it
      // on the server (making isExpandedStored start as undefined)
      // Then, when it hydrates on the client, and we do have access to local
      // storage and, if we've ever saved the expand/collapse state for the
      // filter, will be a defined, boolean value that might be different than
      // expandedOnInit, causing the filter container to render differently.
      if (shouldUseExpandMemory && typeof isExpandedStored === 'boolean') {
        setIsExpandedEphemeral(isExpandedStored)
      }
    },
    // intentionally empty dependency array - we want this to happen only once: on load
    []
  )

  const handleExpand = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { currentTarget } = e
    if (!isExpanded && onExpand) {
      onExpand()
    }
    setIsExpanded(!isExpanded)
    if (!isExpanded && afterExpandAnimation) {
      setTimeout(() => {
        afterExpandAnimation(currentTarget)
      }, 0)
    }
  }

  return { isExpanded, handleExpand }
}

const FilterContainer = ({
  afterExpandAnimation,
  children,
  expandedOnInit,
  inSidebar,
  label,
  onExpand,
  shouldUseExpandMemory,
  tooltip,
}: {
  children: ReactNode
  inSidebar?: boolean
} & ExpandProps) => {
  const { isExpanded, handleExpand } = useExpand({
    label,
    afterExpandAnimation,
    expandedOnInit,
    onExpand,
    shouldUseExpandMemory,
  })
  const tooltipIconRef = useRef<HTMLSpanElement>(null)
  const intl = useIntl()

  const tooltipText = tooltip
    ? tooltip.includes('.')
      ? intl.formatMessage({ id: tooltip })
      : tooltip
    : undefined

  return (
    <Body $inSidebar={inSidebar}>
      <Summary onClick={handleExpand}>
        <Label>
          <FormattedMessage id={label} />
          {tooltipText && (
            <>
              <span ref={tooltipIconRef}>
                <TooltipIcon icon={faCircleInfo} />
              </span>
              {/* oxlint-disable-next-line react/refs */}
              <Tooltip referenceElement={tooltipIconRef.current}>
                {tooltipText}
              </Tooltip>
            </>
          )}
        </Label>
        <FontAwesomeIcon icon={isExpanded ? faAngleUp : faAngleDown} />
      </Summary>
      {isExpanded && <Details>{children}</Details>}
    </Body>
  )
}

export default FilterContainer
